import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { formatNumber, formatPct } from './components/intelTheme'
import { intelApi } from './api'
import { ChartCard, TrendLine, Donut, Bars, CAT_PALETTE } from './components/IntelCharts'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'

const inr = (v) => '₹' + formatNumber(Math.round(v || 0))
const stageColor = (s) =>
  s === 'ALLOCATION' ? 'orange' : s === 'COURIER' ? 'purple' : s === 'CONFIG' ? 'red' : s === 'FINANCE' ? 'blue' : 'grey'

export default function Dashboard() {
  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)
  const [trend, setTrend] = useState([])
  const [ov, setOv] = useState(null)

  const merchantName = useMemo(() => {
    const map = {}
    ;(merchants || []).forEach((m) => { if (m?.merchantId) map[String(m.merchantId)] = m.name })
    return map
  }, [merchants])

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .dashboard()
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    intelApi
      .stuckTrends({ days: 7 })
      .then((res) => {
        if (cancel) return
        const r = res?.data?.responseObj || res?.data?.data || res?.data
        setTrend(r?.points || [])
      })
      .catch(() => {})
    // Reuse the Trends-Overview serviceable-pincodes KPI (same query/number as that page).
    intelApi
      .trendsOverview({})
      .then((res) => {
        if (cancel) return
        setOv(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch(() => {})
    return () => {
      cancel = true
    }
  }, [])

  const cats = useMemo(() => data?.categoryBreakdown || [], [data])
  const merchantsRows = useMemo(() => data?.merchantBreakdown || [], [data])
  const totalStuck = data?.unprocessed || 0

  const catPie = useMemo(() => cats.map((c) => ({ name: c.label || c.code, value: c.count })), [cats])
  const catGmv = useMemo(() => cats.map((c) => ({ name: c.label || c.code, value: Math.round(c.gmv || 0) })), [cats])
  const merBar = useMemo(
    () => merchantsRows.map((m) => ({ name: merchantName[m.merchant] || m.merchant || '—', value: m.count })),
    [merchantsRows, merchantName]
  )
  // Exactly the last 7 days (IST), zero-filled so empty days show 0 (not interpolated across).
  const trend7 = useMemo(() => {
    const by = {}
    ;(trend || []).forEach((p) => { if (p.date) by[p.date] = p.total })
    const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) // YYYY-MM-DD IST
    const base = new Date(todayKey + 'T00:00:00')
    const pad = (n) => String(n).padStart(2, '0')
    const out = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date(base)
      d.setDate(base.getDate() - i)
      const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      out.push({ date: key, total: by[key] || 0 })
    }
    return out
  }, [trend])

  const catColumns = [
    { key: 'label', header: 'Failure Category', render: (r) => r.label || r.code },
    { key: 'stage', header: 'Stage', render: (r) => (r.stage ? <Badge color={stageColor(r.stage)}>{r.stage}</Badge> : '—') },
    { key: 'count', header: 'Orders', render: (r) => formatNumber(r.count) },
    { key: 'pct', header: '% of Total', render: (r) => (totalStuck ? `${Math.round((r.count * 1000) / totalStuck) / 10}%` : '—') },
    { key: 'gmv', header: 'GMV at Risk', render: (r) => inr(r.gmv) },
    { key: 'resolution', header: 'Suggested Resolution', render: (r) => r.resolution || '—', cellStyle: { maxWidth: 340, fontSize: 11.5, whiteSpace: 'normal' } },
  ]
  return (
    <IntelLayout title='Serviceability Intel — Dashboard'>
      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>{err}</Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='red' label='Unprocessed Today' value={formatNumber(data?.unprocessed)} sub={new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} />
        <KpiCard loading={loading} color='orange' label='Rule-based Failures' value={formatNumber(data?.ruleFailures)} sub='Allocation / zone / weight…' />
        <KpiCard loading={loading} color='purple' label='Courier API Rejections' value={formatNumber(data?.apiRejections)} sub='Across couriers' />
        <KpiCard loading={loading}
          color='blue'
          label='Serviceable Pincodes'
          value={formatNumber(ov?.kpis?.servicableToday?.value ?? data?.pincodesCovered)}
          sub={
            ov?.kpis?.servicableToday
              ? `${formatPct(ov.kpis.servicableToday.pctOfUniverse)} of ${formatNumber(ov.universePinCount)} · ${formatNumber(data?.activeCourierAccounts)} CPs`
              : `${formatNumber(data?.activeCourierAccounts)} active courier accounts`
          }
        />
        <KpiCard loading={loading} color='green' label='Active PIN Blocks' value={formatNumber(data?.activePinBlocks)} sub='Distinct pincodes blocked' />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px', mb: 2 }}>
        <ChartCard loading={loading} title='Daily Unprocessed — Last 7 Days'>
          <TrendLine data={trend7} yKey='total' name='Unprocessed' color='#e53935' area height={250} />
        </ChartCard>
        <ChartCard loading={loading} title='Failure Category Breakdown'>
          <Donut data={catPie} height={250} />
        </ChartCard>
        <ChartCard loading={loading} title='Stuck Orders by Merchant'>
          <Bars data={merBar} color='#f57c00' height={250} />
        </ChartCard>
        <ChartCard loading={loading} title='GMV at Risk by Failure Category'>
          <Bars data={catGmv} colors={CAT_PALETTE} height={250} />
        </ChartCard>
      </Box>

      <IntelTable
        title='Failure Summary by Category'
        loading={loading}
        columns={catColumns}
        rows={cats}
        rowKey={(r, i) => r.code || i}
        emptyMsg='No stuck orders'
      />
    </IntelLayout>
  )
}
